/**
 * Utility functions for disability calculator
 */
import { MBCBPRates, NECRates, TaxBrackets, TFPRateEntry } from '../Models/disability-calculator';

// Debug flag (set this based on your environment)
const DEBUG = false; // TODO: Replace with actual environment variable when available

// Debug function
export function debug(...args: any[]) {
  if (DEBUG) {
    console.log('[DEBUG]', ...args);
  }
}

// Simple rounding to 5 decimal places
export function roundTo5Decimals(value: number): number {
  return Math.round(value * 100000) / 100000;
};

/**
 * Parse MM/DD/YYYY date string to UTC Date to avoid timezone issues
 * @param dateString - Date string in MM/DD/YYYY format
 * @returns UTC Date object
 */
export function parseUTCDate(dateString: string): Date {
  if (!dateString) throw new Error('Date string is required');
  const [month, day, year] = dateString.split('/').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
}

/**
 * Format currency with 2 decimal places
 * @param amount - The amount to format
 * @returns Formatted currency string or '-' if amount is 0, null, or undefined
 */
export function formatCurrency(amount: number): string {
  if (amount === 0 || amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format currency as integer
 * @param amount - The amount to format
 * @returns Formatted currency string or '-' if amount is 0, null, or undefined
 */
export function formatCurrencyInteger(amount: number): string {
  if (amount === 0 || amount === null || amount === undefined) return "-";

  const rounded = Math.round(amount);

  if (rounded < 0) {
    // Format positive and then wrap with parentheses
    return `(${new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(rounded))})`;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
}

/**
 * Parse percentage from string
 * @param percentageStr - Percentage string (e.g., "11.5%")
 * @returns Parsed percentage as a number
 */
export function parsePercentage(percentageStr: string): number {
  if (!percentageStr) return 0;
  // Remove % sign and parse
  const cleanStr = percentageStr.toString().replace("%", "").trim();
  const value = parseFloat(cleanStr);
  return isNaN(value) ? 0 : value / 100;
}

/**
 * Convert value to number
 * @param v - Value to convert
 * @returns Converted number
 */
export function toNumber(v: string | number | null | undefined): number {
  if (v === "-" || v == null) return 0;

  const strVal = String(v).trim();
  const num = Number(strVal.replace(/[^0-9.-]+/g, "")) || 0;

  return strVal.includes("%") ? num / 100 : num;
}

/**
 * Ceiling function for 2 decimal places
 * @param n - Number to round up
 * @returns Rounded up number with 2 decimal places
 */
export function ceil2(n: number): number {
  return Math.ceil(n * 100) / 100;
}

/**
 * Get year value or, if missing, the "Default" value.
 * @param map - Object with year keys and a "Default" key
 * @param year - Year to look up
 * @returns Value for the given year or the "Default" value
 */
export function getYearValueOrDefault(
  map: NECRates | MBCBPRates,
  year: string
): number {
  if (Object.prototype.hasOwnProperty.call(map, year)) {
    return parsePercentage(map[year].toString());
  }
  if (Object.prototype.hasOwnProperty.call(map, "Default")) {
    return parsePercentage(map["Default"].toString());
  }
  throw new Error(`Key "${year}" not found and "Default" is missing in map.`);
}

/**
 * Generate tax brackets for multiple years
 * @returns Object with tax brackets for each year
 */
export function generateTaxBrackets(endYear: number, taxBrackets: TaxBrackets): { [key: number]: number[] } {

  let currentYear = new Date().getUTCFullYear();

  let iternation = endYear - currentYear;

  for (let i = 1; i <= iternation; i++) {
    const previousYear = 2025 + i - 1;
    const currentYear = 2025 + i;

    const previousBrackets = taxBrackets[previousYear];
    const newBrackets = previousBrackets.map((value) =>
      Math.ceil(value * 1.028)
    );

    taxBrackets[currentYear] = newBrackets;
  }

  return taxBrackets;
}

export function getTFPRate(
  rateCode: string,
  year: number,
  tfpRates: TFPRateEntry[]
): number {
  const tfpTable = getTFPRatesTable(tfpRates);

  if (!tfpTable[rateCode]) {
    return 0;
  }

  if (!tfpTable[rateCode][year]) {
    // If year not in table, use last available year if requested year is greater
    const availableYears = Object.keys(tfpTable[rateCode])
      .map((y) => parseInt(y))
      .sort();
    const lastYear = Math.max(...availableYears);

    if (year > lastYear) {
      // Use last available year for future years
      return tfpTable[rateCode][lastYear];
    } else {
      // For past years, find closest available year
      const closestYear = availableYears.reduce((prev, curr) =>
        Math.abs(curr - year) < Math.abs(prev - year) ? curr : prev
      );
      return tfpTable[rateCode][closestYear];
    }
  }

  return tfpTable[rateCode][year];
}

function getTFPRatesTable(tfpRatesFromAPI: TFPRateEntry[]): { [key: string]: { [year: number]: number } } {
  // Check if API data is available
  if (!tfpRatesFromAPI || tfpRatesFromAPI.length === 0) {
    return {};
  }

  const tfpTable: { [key: string]: { [year: number]: number } } = {};
  tfpRatesFromAPI.forEach((rate) => {
    tfpTable[rate.YOS.toString()] = {
      2025: rate.Year2025,
      2026: rate.Year2026,
      2027: rate.Year2027,
      2028: rate.Year2028,
    };
  });
  return tfpTable;
}

/**
 * Safely increment date to the first day of the next month
 * Avoids JavaScript Date bugs with day-of-month conflicts (e.g., Jan 31 -> Feb 31)
 * 
 * @param date - The date to increment
 * @returns New Date object set to the 1st day of the next month
 */
export function incrementToNextMonthStart(date: Date): Date {
  const nextDate = new Date(date);
  
  // Always set to 1st of current month first to avoid day conflicts
  nextDate.setUTCDate(1);
  
  // Then increment to next month - this safely handles year rollover
  nextDate.setUTCMonth(nextDate.getUTCMonth() + 1);
  
  return nextDate;
}
